import { ResultsTestRunModel } from 'src/app/models/results';
import { OpenSearchAnalyzerFlatDocument } from 'src/app/models/open-search-analyzer-flat-document';
import { parseCommentMap } from 'src/app/utils/comment-map.utils';

/**
 * Transforms Results API test run models into the OpenSearchAnalyzerFlatDocument format
 * used by the dashboard, charts, and table components.
 * @param models
 */
export function convertResultsTestRunModelsToFlatDocuments(models: ResultsTestRunModel[]): OpenSearchAnalyzerFlatDocument[] {
	const documents: OpenSearchAnalyzerFlatDocument[] = [];

	for (const model of models) {
		const testRun = model.testRun;
		if (!testRun) continue;

		const mainSuiteName = model.testSuite?.name || model.testSpecification?.name || '';
		const mainSuiteItemNo = model.testSuite?.itemNo || model.testSpecification?.itemNo || '';
		const uid = testRun.testRunUid;

		const mainSuiteProperties: Record<string, string> = {
			TestRunUid: testRun.testRunUid,
			RegBy: testRun.regBy || '',
			VinNo: testRun.vinNo || '',
			Configuration: testRun.configuration || '',
			Environment: testRun.environment || '',
			Classification: testRun.classification || '',
			ClassificationReason: String(testRun.classificationReason ?? ''),
			ClassificationComment: testRun.classificationComment || '',
			Status: testRun.status || '',
			TestSuiteItemNo: mainSuiteItemNo,
		};

		if (testRun.sop) mainSuiteProperties['Sop'] = testRun.sop;
		if (testRun.testPeriod) mainSuiteProperties['TestPeriod'] = testRun.testPeriod;
		if (model.testRunType) mainSuiteProperties['TestRunType'] = model.testRunType === 110 ? 'Test Suite' : model.testRunType === 410 ? 'Test Specification' : String(model.testRunType);

		if (model.testSuite) {
			mainSuiteProperties['TestSuiteUid'] = model.testSuite.testSuiteUid;
			mainSuiteProperties['TestSuiteVersion'] = String(model.testSuiteVersion || model.testSuite.version || '');
		}
		if (model.testSpecification) {
			mainSuiteProperties['TestSpecificationUid'] = model.testSpecification.uid;
			if (!mainSuiteProperties['TestSuiteVersion']) {
				mainSuiteProperties['TestSuiteVersion'] = String(model.testSuiteVersion || model.testSpecification.version || '');
			}
		}

		if (model.ecuAndSwIdentifiers?.length > 0) {
			const ecuNames = model.ecuAndSwIdentifiers.map(e => e.name).join(', ');
			mainSuiteProperties['Ecu'] = ecuNames;
			const swVersions = model.ecuAndSwIdentifiers
				.map(e => e.swIdentifiers)
				.filter(Boolean)
				.join(', ');
			if (swVersions) mainSuiteProperties['SoftwareVersion'] = swVersions;
		}

		const testResults = testRun.testResults || [];
		const totalTests = testResults.length;
		const failures = testResults.filter(r => r.result?.toLowerCase() === 'failed' || r.result?.toLowerCase() === 'fail').length;
		const skipped = testResults.filter(r => mapResultStatus(r.result) === 'skipped').length;

		const classification = resolveClassification(testRun.classification, testRun.classificationReason);

		// Emit a single sub-suite level document per test run (analyzerTestCaseUid = null).
		// The dashboard uses these to identify unique suites in the table.
		documents.push({
			uid,
			analyzerEnvironment: 'results',
			testSuiteName: mainSuiteName,
			analyzerErrors: 0,
			analyzerFailures: failures,
			analyzerSkipped: skipped,
			analyzerTests: totalTests,
			analyzerExecutionTime: null,
			startTime: testRun.executionTime ? new Date(testRun.executionTime) : null,
			analyzerHostname: '',
			testPeriod: testRun.testPeriod || null,
			testPeriodNumber: null,
			analyzerVehicleConfiguration: testRun.environment || null,
			analyzerArchived: false,
			subTestSuiteName: null,
			analyzerSubTestSuiteUid: null,
			analyzerSubTestSuiteDescription: null,
			subTestSuiteInlineComment: null,
			subTestSuiteInternalComment: null,
			subTestSuiteClassification: null,
			analyzerTestCaseUid: null,
			testCaseName: '',
			executionTime: 0,
			analyzerStatus: '',
			analyzerStatusMessage: null,
			analyzerLeftHandSide: null,
			analyzerRightHandSide: null,
			analyzerOperator: null,
			s3Key: null,
			jiraTickets: null,
			inlineComment: null,
			internalComment: null,
			classification: null,
			mainSuiteJiraTickets: null,
			mainSuiteInlineComment: null,
			mainSuiteInternalComment: null,
			mainSuiteClassification: classification || null,
			mainSuiteProperties,
			mainSuiteS3Key: null,
			createdAt: testRun.regTime ? new Date(testRun.regTime) : null,
			properties: null,
		});

		// If testResults are populated (not null), also emit test case level documents
		// so the dashboard can count them and the test case view works.
		// Note: resultsTestRunModelByDates does NOT populate testResults (returns null).
		if (testResults.length > 0) {
			for (const result of testResults) {
				const status = mapResultStatus(result.result);
				const suiteUid = result.testSuiteUid || null;
				const suiteName = result.testSuiteName || mainSuiteName;
				const tcProperties: Record<string, string> = {};
				if (result.testCaseVersion != null) tcProperties['TestCaseVersion'] = String(result.testCaseVersion);
				if (result.failedReason) tcProperties['FailedReason'] = String(result.failedReason);
				if (result.failedBy) tcProperties['FailedBy'] = result.failedBy;

				documents.push({
					uid,
					analyzerEnvironment: 'results',
					testSuiteName: mainSuiteName,
					analyzerErrors: 0,
					analyzerFailures: failures,
					analyzerSkipped: skipped,
					analyzerTests: totalTests,
					analyzerExecutionTime: null,
					startTime: testRun.executionTime ? new Date(testRun.executionTime) : null,
					analyzerHostname: '',
					testPeriod: testRun.testPeriod || null,
					testPeriodNumber: null,
					analyzerVehicleConfiguration: testRun.environment || null,
					analyzerArchived: false,
					subTestSuiteName: suiteName !== mainSuiteName ? suiteName : null,
					analyzerSubTestSuiteUid: suiteUid,
					analyzerSubTestSuiteDescription: null,
					subTestSuiteInlineComment: null,
					subTestSuiteInternalComment: null,
					subTestSuiteClassification: null,
					analyzerTestCaseUid: result.testCaseUid || null,
					testCaseName: result.testCaseName || '',
					executionTime: 0,
					analyzerStatus: status,
					analyzerStatusMessage: result.failedReasonComment || null,
					analyzerLeftHandSide: null,
					analyzerRightHandSide: null,
					analyzerOperator: null,
					s3Key: null,
					jiraTickets: null,
					inlineComment: parseCommentMap(result.resultComment),
					internalComment: null,
					classification: null,
					mainSuiteJiraTickets: null,
					mainSuiteInlineComment: null,
					mainSuiteInternalComment: null,
					mainSuiteClassification: classification || null,
					mainSuiteProperties,
					mainSuiteS3Key: null,
					createdAt: testRun.regTime ? new Date(testRun.regTime) : null,
					properties: Object.keys(tcProperties).length > 0 ? tcProperties : null,
				});
			}
		}
	}

	return documents;
}

function mapResultStatus(result: string): string {
	if (!result || result.trim() === '') return 'skipped';
	const lower = result.toLowerCase();
	if (lower === 'passed' || lower === 'pass') return 'passed';
	if (lower === 'failed' || lower === 'fail') return 'failure';
	if (lower === 'not executed') return 'skipped';
	return lower;
}

function resolveClassification(classification: unknown, reason: unknown): string {
	const classValue = mapClassificationEnum(classification);
	if (!classValue) return '';
	if (classValue === 'NotValid') {
		const reasonValue = mapClassificationReasonEnum(reason);
		return reasonValue || 'Not Valid';
	}
	return classValue;
}

function mapClassificationEnum(value: unknown): string {
	if (value === 0 || value === '0' || value === 'Valid' || value === 'valid') return 'Valid';
	if (value === 1 || value === '1' || value === 'NotValid' || value === 'notvalid' || value === 'not valid' || value === 'Not Valid') return 'NotValid';
	return '';
}

function mapClassificationReasonEnum(value: unknown): string {
	if (value === 1 || value === '1' || value === 'Environment' || value === 'environment') return 'Environment';
	if (value === 2 || value === '2' || value === 'Script' || value === 'script') return 'Script';
	return '';
}
